import apiClient from './apiClient';

export interface Task {
  id: string;
  title: string;
  taskCode?: string;
  taskType?: string;
  description: string | null;
  status: string;
  lifecycleStatus?: string;
  workerLifecycleStatus?: string | null;
  workerActualStartDate?: string | null;
  workerCompletedAt?: string | null;
  workerActualSeconds?: number | null;
  dispatchWorkers?: TaskDispatchWorker[];
  completedDispatchWorkers?: TaskDispatchWorker[];
  dispatchWorkersAllSubmitted?: boolean | null;
  paymentStatus?: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  startDate: string | null;
  actualStartDate?: string | null;
  dueDate: string | null;
  completionDate: string | null;
  completedBy?: {
    id: string;
    name?: string | null;
    email?: string | null;
  } | null;
  completedByName?: string | null;
  completedAt?: string | null;
  reviewedBy?: {
    id: string;
    name?: string | null;
    email?: string | null;
  } | null;
  reviewedByName?: string | null;
  reviewedAt?: string | null;
  reviewVerdict?: 'approved' | 'failed' | string | null;
  percentComplete: number;
  estimatedHours: number | null;
  actualHours: number | null;
  materialsReady?: boolean;
  storageLocationId?: string | null;
  /**
   * Latest blocked_reason recorded against a transition to lifecycleStatus=blocked.
   * Only populated when lifecycleStatus is 'BLOCKED'. One of the BlockedReason
   * enum values: predecessor_incomplete, materials_shortage*, calendar_blocked,
   * crew_unavailable, equipment_unavailable, weather, drawing_conflict,
   * drawing_superseded, safety_standdown, hse_incident, hse_stand_down, supervisor_hold, pm_hold.
   */
  blockedReason?: string | null;
  rejectionReason?: string | null;
  assignedToId: string | null;
  assignedTo?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  pmIssuedToSupervisorId?: string | null;
  pmIssuedToSupervisor?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  pmIssuedById?: string | null;
  pmIssuer?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  pmIssuedAt?: string | null;
  teamId: string | null;
  team?: {
    id: string;
    name: string;
  };
  teamAssignments?: Array<{
    id?: string;
    taskId?: string;
    task_id?: string;
    teamId?: string;
    team_id?: string;
    lifecycleStatus?: string;
    lifecycle_status?: string;
    paymentStatus?: string | null;
    payment_status?: string | null;
    team?: {
      id: string;
      name: string;
    };
  }>;
  projectId: string | null;
  project?: {
    id: string;
    name: string;
  };
  phaseId: string | null;
  phase?: {
    id: string;
    name: string;
  };
  blockId: string | null;
  buildingId: string | null;
  unitId: string | null;
  workLocation?: string | null;
  workLocationId?: string | null;
  workLocationRef?: { id: string; name: string } | null;
  locationId?: string | null;
  bqSequence?: number | null;
  methodStatement?: string | null;
  qcSpecification?: string | null;
  materialHandling?: string | null;
  specifications?: Array<{ text: string; done: boolean }>;
  conditions?: string[];
  component?: {
    id: string;
    code: string;
    name: string;
    unit?: {
      id: string;
      code: string;
      name: string;
      unitType?: string | null;
    } | null;
  } | null;
  resourcesRequired: any[];
  attachments?: TaskAttachment[];
  defects?: TaskDefect[];
  history?: any[];
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TaskDispatchWorker {
  id?: string | null;
  userId?: string | null;
  name?: string | null;
  badgeCode?: string | null;
  teamId?: string | null;
  trade?: string | null;
  userEmail?: string | null;
  selectionType?: string | null;
  lifecycleStatus?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  actualSeconds?: number | string | null;
}

export interface TaskAttachment {
  id: string;
  taskId?: string;
  fileName: string;
  fileType: string;
  fileUrl: string;
  fileSizeKb?: number | null;
  attachmentType?: string | null;
  description?: string | null;
  uploadedAt?: string;
}

export interface TaskDefect {
  id: string;
  title: string;
  description?: string | null;
  severity?: string;
  status?: string;
  defectType?: string | null;
  remedialTaskId?: string | null;
  assignedToTeamId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface TaskFilters {
  status?: string;
  priority?: string;
  assignedToId?: string;
  workerId?: string;
  teamId?: string;
  projectId?: string;
  phaseId?: string;
  blockId?: string;
  buildingId?: string;
  unitId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
  supervisorHandoff?: boolean;
}

export interface TasksResponse {
  tasks: Task[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

type ApiEnvelope<T> = { success?: boolean; data?: T; meta?: any };

interface BackendTask {
  id: string;
  name?: string;
  title?: string;
  description?: string | null;
  status?: string;
  priority?: string;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  actualEndDate?: string | null;
  completionPercentage?: number;
  estimatedDurationHours?: number | null;
  actualDurationHours?: number | null;
  teamId?: string | null;
  team?: {
    id: string;
    name: string;
  };
  projectId?: string | null;
  project?: {
    id: string;
    name: string;
  };
  phaseId?: string | null;
  phase?: {
    id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
  [key: string]: any;
}

const BACKEND_LIFECYCLE_TO_FRONTEND: Record<string, string> = {
  draft: 'DRAFT',
  scheduled: 'SCHEDULED',
  waiting: 'WAITING',
  blocked: 'BLOCKED',
  ready: 'READY',
  in_progress: 'IN_PROGRESS',
  paused: 'PAUSED',
  inspection_pending: 'INSPECTION_PENDING',
  inspection_failed: 'INSPECTION_FAILED',
  inspection_passed: 'INSPECTION_PASSED',
  cancelled: 'CANCELLED',
};

const BACKEND_LEGACY_STATUS_TO_FRONTEND: Record<string, string> = {
  stores_picking: 'STORES_PICKING',
  ready_for_pickup: 'READY_FOR_PICKUP',
  work_complete: 'WORK_COMPLETE',
  approved: 'APPROVED',
  rejected: 'REJECTED',
  payment_pending: 'PAYMENT_PENDING',
  paid: 'PAID',
  closed: 'CLOSED',
  blocked_dependency: 'BLOCKED_DEPENDENCY',
  blocked_shortage: 'BLOCKED_SHORTAGE',
  on_hold: 'ON_HOLD',
};

const LIFECYCLE_STATUS_TO_BACKEND: Record<string, string> = {
  DRAFT: 'draft',
  SCHEDULED: 'scheduled',
  WAITING: 'waiting',
  BLOCKED: 'blocked',
  READY: 'ready',
  IN_PROGRESS: 'in_progress',
  PAUSED: 'paused',
  INSPECTION_PENDING: 'inspection_pending',
  INSPECTION_FAILED: 'inspection_failed',
  INSPECTION_PASSED: 'inspection_passed',
  CANCELLED: 'cancelled',
};

const PRIORITY_TO_FRONTEND: Record<string, Task['priority']> = {
  low: 'LOW',
  normal: 'MEDIUM',
  medium: 'MEDIUM',
  high: 'HIGH',
  critical: 'URGENT',
  urgent: 'URGENT',
};

const PRIORITY_TO_BACKEND: Record<string, string> = {
  LOW: 'low',
  MEDIUM: 'normal',
  HIGH: 'high',
  URGENT: 'critical',
};

const unwrapApiData = <T>(response: unknown): T => {
  const maybeAxiosResponse = response as { data?: unknown; status?: number };
  const body = typeof maybeAxiosResponse?.status === 'number'
    ? maybeAxiosResponse.data
    : response;
  const envelope = body as ApiEnvelope<T>;
  return envelope && Object.prototype.hasOwnProperty.call(envelope, 'data')
    ? envelope.data as T
    : body as T;
};

const normalizeStatus = (status?: string): string => {
  if (!status) return 'DRAFT';
  const backendStatus = status.toLowerCase();
  return BACKEND_LIFECYCLE_TO_FRONTEND[backendStatus]
    || BACKEND_LEGACY_STATUS_TO_FRONTEND[backendStatus]
    || status.toUpperCase();
};

const normalizePriority = (priority?: string): Task['priority'] => {
  if (!priority) return 'MEDIUM';
  return PRIORITY_TO_FRONTEND[priority.toLowerCase()] || 'MEDIUM';
};

const toFrontendTask = (task: BackendTask): Task => ({
  id: task.id,
  title: task.title || task.name || 'Untitled task',
  taskCode: task.taskCode,
  taskType: task.taskType,
  description: task.description ?? null,
  status: normalizeStatus(task.status),
  lifecycleStatus: normalizeStatus(task.lifecycleStatus ?? task.status),
  workerLifecycleStatus: (task as any).workerLifecycleStatus ? normalizeStatus((task as any).workerLifecycleStatus) : null,
  workerActualStartDate: (task as any).workerActualStartDate ?? (task as any).worker_actual_start_date ?? null,
  workerCompletedAt: (task as any).workerCompletedAt ?? (task as any).worker_completed_at ?? null,
  workerActualSeconds: Number((task as any).workerActualSeconds ?? (task as any).worker_actual_seconds ?? 0) || 0,
  dispatchWorkers: Array.isArray((task as any).dispatchWorkers) ? (task as any).dispatchWorkers : [],
  completedDispatchWorkers: Array.isArray((task as any).completedDispatchWorkers) ? (task as any).completedDispatchWorkers : [],
  dispatchWorkersAllSubmitted: typeof (task as any).dispatchWorkersAllSubmitted === 'boolean' ? (task as any).dispatchWorkersAllSubmitted : null,
  paymentStatus: task.paymentStatus,
  priority: normalizePriority(task.priority),
  startDate: task.startDate ?? task.plannedStartDate ?? null,
  actualStartDate: task.actualStartDate ?? null,
  dueDate: task.dueDate ?? task.plannedEndDate ?? null,
  completionDate: task.completionDate ?? task.actualEndDate ?? null,
  completedBy: task.completedBy ?? task.completed_by ?? null,
  completedByName: task.completedByName ?? task.completed_by_name ?? null,
  completedAt: task.completedAt ?? task.completed_at ?? null,
  reviewedBy: task.reviewedBy ?? task.reviewed_by ?? null,
  reviewedByName: task.reviewedByName ?? task.reviewed_by_name ?? null,
  reviewedAt: task.reviewedAt ?? task.reviewed_at ?? null,
  reviewVerdict: task.reviewVerdict ?? task.review_verdict ?? null,
  percentComplete: task.percentComplete ?? task.completionPercentage ?? 0,
  estimatedHours: task.estimatedHours ?? task.estimatedDurationHours ?? null,
  actualHours: task.actualHours ?? task.actualDurationHours ?? null,
  materialsReady: typeof task.materialsReady === 'boolean' ? task.materialsReady : undefined,
  storageLocationId: task.storageLocationId ?? null,
  blockedReason: (task as any).blockedReason ?? (task as any).blocked_reason ?? null,
  rejectionReason: (task as any).rejectionReason ?? (task as any).rejection_reason ?? null,
  assignedToId: task.assignedToId ?? null,
  assignedTo: task.assignedTo,
  pmIssuedToSupervisorId: task.pmIssuedToSupervisorId ?? task.pm_issued_to_supervisor_id ?? null,
  pmIssuedToSupervisor: task.pmIssuedToSupervisor ?? task.pm_issued_to_supervisor ?? null,
  pmIssuedById: task.pmIssuedById ?? task.pm_issued_by_id ?? null,
  pmIssuer: task.pmIssuer ?? task.pm_issuer ?? null,
  pmIssuedAt: task.pmIssuedAt ?? task.pm_issued_at ?? null,
  teamId: task.teamId ?? null,
  team: task.team,
  teamAssignments: task.teamAssignments ?? task.team_assignments ?? [],
  projectId: task.projectId ?? null,
  project: task.project,
  phaseId: task.phaseId ?? null,
  phase: task.phase,
  blockId: task.blockId ?? null,
  buildingId: task.buildingId ?? null,
  unitId: task.unitId ?? null,
  workLocation: task.workLocation ?? null,
  workLocationId: task.workLocationId ?? null,
  workLocationRef: task.workLocationRef ?? null,
  locationId: task.locationId ?? task.location_id ?? null,
  bqSequence: task.bqSequence ?? task.bq_sequence ?? null,
  methodStatement: task.methodStatement ?? task.method_statement ?? null,
  qcSpecification: task.qcSpecification ?? task.qc_specification ?? null,
  materialHandling: task.materialHandling ?? task.material_handling ?? null,
  specifications: Array.isArray(task.specifications)
    ? task.specifications.map((s: any) =>
        typeof s === 'string'
          ? { text: s, done: false }
          : { text: String(s?.text ?? ''), done: Boolean(s?.done) }
      )
    : [],
  conditions: Array.isArray(task.conditions) ? task.conditions : [],
  component: task.component ?? null,
  resourcesRequired: task.resourcesRequired ?? [],
  attachments: Array.isArray(task.attachments) ? task.attachments : [],
  defects: task.defects ?? [],
  history: task.history ?? [],
  notes: task.notes ?? null,
  createdAt: task.createdAt,
  updatedAt: task.updatedAt,
});

const toBackendTaskData = (taskData: Partial<Task>): Record<string, unknown> => ({
  ...taskData,
  name: taskData.title,
  priority: taskData.priority ? PRIORITY_TO_BACKEND[taskData.priority] : undefined,
  status: taskData.status
    ? LIFECYCLE_STATUS_TO_BACKEND[String(taskData.status).toUpperCase()]
    : undefined,
  plannedStartDate: taskData.startDate,
  plannedEndDate: taskData.dueDate,
  completionPercentage: taskData.percentComplete,
});

class TasksService {
  /**
   * Get all tasks with optional filters
   */
  async getTasks(filters?: TaskFilters): Promise<TasksResponse> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `/tasks?${queryString}` : '/tasks';

    const response = await apiClient.get<ApiEnvelope<TasksResponse>>(url);
    const data = unwrapApiData<TasksResponse>(response);
    return {
      ...data,
      tasks: data.tasks.map(toFrontendTask),
    };
  }

  /**
   * Get a single task by ID
   */
  async getTaskById(id: string): Promise<Task> {
    const response = await apiClient.get<ApiEnvelope<BackendTask>>(`/tasks/${id}`);
    return toFrontendTask(unwrapApiData<BackendTask>(response));
  }

  /**
   * Create a new task
   */
  async createTask(taskData: Partial<Task>): Promise<Task> {
    const response = await apiClient.post<ApiEnvelope<BackendTask>>('/tasks', toBackendTaskData(taskData));
    return toFrontendTask(unwrapApiData<BackendTask>(response));
  }

  /**
   * Update an existing task
   */
  async updateTask(id: string, taskData: Partial<Task>): Promise<Task> {
    const response = await apiClient.put<ApiEnvelope<BackendTask>>(`/tasks/${id}`, toBackendTaskData(taskData));
    return toFrontendTask(unwrapApiData<BackendTask>(response));
  }

  /**
   * Delete a task
   */
  async deleteTask(id: string): Promise<void> {
    await apiClient.delete<void>(`/tasks/${id}`);
  }

  /**
   * Update task status
   */
  async updateTaskStatus(id: string, status: string): Promise<Task> {
    const response = await apiClient.patch<ApiEnvelope<BackendTask>>(`/tasks/${id}/status`, {
      newStatus: LIFECYCLE_STATUS_TO_BACKEND[status.toUpperCase()] || status.toLowerCase(),
    });
    return toFrontendTask(unwrapApiData<BackendTask>(response));
  }

  async updateDispatchedWorkerTaskStatus(id: string, status: string): Promise<Task> {
    const response = await apiClient.patch<ApiEnvelope<BackendTask>>(`/tasks/${id}/worker-status`, {
      newStatus: LIFECYCLE_STATUS_TO_BACKEND[status.toUpperCase()] || status.toLowerCase(),
    });
    return toFrontendTask(unwrapApiData<BackendTask>(response));
  }

  /**
   * Override task lifecycle status as PM/Admin.
   */
  async overrideTaskStatus(
    id: string,
    payload: {
      overrideType: string;
      reasonCode: string;
      notes: string;
      fieldEvidenceId?: string;
      documentId?: string;
      pmSignature?: string;
    }
  ): Promise<Task> {
    const response = await apiClient.post<ApiEnvelope<BackendTask>>(`/tasks/${id}/override`, payload);
    return toFrontendTask(unwrapApiData<BackendTask>(response));
  }

  /**
   * Quick-add a defect to a task. Bypasses the inspection-fail auto-create
   * flow — for ad-hoc PM/supervisor logging.
   */
  async addDefect(
    id: string,
    payload: { title: string; description?: string; severity?: 'low' | 'medium' | 'high' | 'critical' }
  ): Promise<unknown> {
    const response = await apiClient.post<ApiEnvelope<unknown>>(`/tasks/${id}/defects`, payload);
    return unwrapApiData<unknown>(response);
  }

  /**
   * Assign task to user
   */
  async assignTeam(
    id: string,
    teamId: string,
    options: { overrideOverAllocation?: boolean; overrideReason?: string } = {}
  ): Promise<Task> {
    const response = await apiClient.patch<ApiEnvelope<BackendTask | { task: BackendTask }>>(`/tasks/${id}/assign`, {
      teamId,
      ...options,
    });
    const data = unwrapApiData<BackendTask | { task: BackendTask }>(response);
    return toFrontendTask('task' in data ? data.task : data);
  }

  async issueToSupervisor(id: string, payload: { supervisorUserId: string; notes?: string | null }): Promise<Task> {
    const response = await apiClient.post<ApiEnvelope<BackendTask>>(`/tasks/${id}/issue-to-supervisor`, payload);
    return toFrontendTask(unwrapApiData<BackendTask>(response));
  }

  async recallFromSupervisor(id: string, payload: { notes?: string | null } = {}): Promise<Task> {
    const response = await apiClient.post<ApiEnvelope<BackendTask>>(`/tasks/${id}/recall-from-supervisor`, payload);
    return toFrontendTask(unwrapApiData<BackendTask>(response));
  }

  /**
   * Update task progress
   */
  async updateTaskProgress(id: string, percentComplete: number): Promise<Task> {
    const response = await apiClient.patch<ApiEnvelope<BackendTask>>(`/tasks/${id}/progress`, { percentComplete });
    return toFrontendTask(unwrapApiData<BackendTask>(response));
  }

  /**
   * Multi-team task assignments — list each team's per-row lifecycle_status.
   * The parent task's rollup status derives from these via
   * computeTaskRollupStatus in the backend.
   */
  async getTaskTeamAssignments(taskId: string): Promise<Array<{
    id: string;
    taskId: string;
    teamId: string;
    teamName: string;
    lifecycleStatus: string;
    assignedAt: string;
    completedAt: string | null;
  }>> {
    const response: any = await apiClient.get(`/tasks/${taskId}/team-assignments`);
    const rows = (response?.data ?? []) as any[];
    return rows.map((r) => ({
      id: r.id,
      taskId: r.taskId ?? r.task_id,
      teamId: r.teamId ?? r.team_id,
      teamName: r.team?.name ?? r.teamName ?? '—',
      lifecycleStatus: r.lifecycleStatus ?? r.lifecycle_status,
      assignedAt: r.assignedAt ?? r.assigned_at,
      completedAt: r.completedAt ?? r.completed_at ?? null,
    }));
  }

  /**
   * Add an additional team to a task.
   */
  async addTaskTeamAssignment(
    taskId: string,
    teamId: string,
    options: { overrideOverAllocation?: boolean; overrideReason?: string } = {}
  ): Promise<Task> {
    const response = await apiClient.post<ApiEnvelope<BackendTask | { task: BackendTask }>>(
      `/tasks/${taskId}/team-assignments`,
      { teamId, ...options }
    );
    const data = unwrapApiData<BackendTask | { task: BackendTask }>(response);
    return toFrontendTask('task' in data ? data.task : data);
  }

  /**
   * Change one assigned team's status. Backend recomputes the parent task's
   * rollup status from all assignment rows.
   */
  async updateTaskTeamAssignmentStatus(
    taskId: string,
    assignmentId: string,
    lifecycleStatus: string
  ): Promise<Task> {
    const response = await apiClient.patch<ApiEnvelope<BackendTask>>(
      `/tasks/${taskId}/team-assignments/${assignmentId}/status`,
      { lifecycleStatus }
    );
    return toFrontendTask(unwrapApiData<BackendTask>(response));
  }
}

export default new TasksService();
